import { useState, useEffect } from "react";
import { Mail, Video, Send, Loader2, Check, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [gmailUser, setGmailUser] = useState("");
  const [gmailAppPassword, setGmailAppPassword] = useState("");
  const [passwordSet, setPasswordSet] = useState(false);
  const [zoomLink, setZoomLink] = useState("");
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(`${API}/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGmailUser(res.data.gmail_user || "");
        setPasswordSet(res.data.gmail_app_password_set || false);
        setZoomLink(res.data.zoom_link || "");
      } catch (error) {
        // Settings not yet configured
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = { gmail_user: gmailUser, zoom_link: zoomLink };
      if (gmailAppPassword) {
        payload.gmail_app_password = gmailAppPassword;
      }
      await axios.post(`${API}/admin/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Settings saved!");
      setPasswordSet(true);
      setGmailAppPassword("");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Enter an email to send test to");
      return;
    }
    setTesting(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/admin/test-email`, { to_email: testEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test email sent to ${testEmail}!`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to send test email";
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-2" data-testid="settings-title">Settings</h1>
      <p className="text-[#4A4A4A] mb-8">Configure email notifications and class links</p>

      {/* Zoom Link */}
      <div className="brutalist-card p-6 mb-6" data-testid="zoom-settings">
        <h2 className="font-bold text-lg flex items-center gap-2 mb-4">
          <Video className="w-5 h-5" /> Class Meeting Link
        </h2>
        <p className="text-sm text-[#4A4A4A] mb-4">
          This link will be sent to students when they book a class.
        </p>
        <Input
          value={zoomLink}
          onChange={(e) => setZoomLink(e.target.value)}
          placeholder="https://zoom.us/j/123456789 or Google Meet link"
          className="brutalist-input"
          data-testid="zoom-link-input"
        />
      </div>

      {/* Email Configuration */}
      <div className="brutalist-card p-6 mb-6" data-testid="email-settings">
        <h2 className="font-bold text-lg flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5" /> Email Configuration
        </h2>
        <p className="text-sm text-[#4A4A4A] mb-4">
          Booking confirmation emails will be sent from this Gmail account. You need a Gmail App Password (not your regular password).
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-bold uppercase tracking-wider block mb-1">Gmail Address</label>
            <Input
              type="email"
              value={gmailUser}
              onChange={(e) => setGmailUser(e.target.value)}
              placeholder="officialvirtuallyverbal@gmail.com"
              className="brutalist-input"
              data-testid="gmail-user-input"
            />
          </div>

          <div>
            <label className="text-sm font-bold uppercase tracking-wider block mb-1">
              App Password {passwordSet && <span className="text-[#D6E6FA] font-normal">(already set)</span>}
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={gmailAppPassword}
                onChange={(e) => setGmailAppPassword(e.target.value)}
                placeholder={passwordSet ? "Leave blank to keep current" : "xxxx xxxx xxxx xxxx"}
                className="brutalist-input pr-12"
                data-testid="gmail-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-[#4A4A4A] mt-1">
              Go to Google Account &rarr; Security &rarr; 2-Step Verification &rarr; App Passwords to generate one
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={saveSettings}
        disabled={saving}
        className="brutalist-btn-primary flex items-center gap-2 mb-8"
        data-testid="save-settings-btn"
      >
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
        Save Settings
      </button>

      {/* Test Email */}
      {passwordSet && (
        <div className="brutalist-card p-6 bg-[#F0EFEB]" data-testid="test-email-section">
          <h3 className="font-bold mb-3">Send Test Email</h3>
          <div className="flex gap-3">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              className="brutalist-input flex-1"
              data-testid="test-email-input"
            />
            <button
              onClick={sendTestEmail}
              disabled={testing}
              className="brutalist-btn-primary flex items-center gap-2"
              data-testid="send-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Test
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
